import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, LayoutGrid, List, Search, SlidersHorizontal, Upload } from "lucide-react";
import StudentList from "./StudentList";
import StudentGrid from "./StudentGrid";
import { useBranch } from "../../contexts/BranchContext";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import TransferStudentsModal from "../Common/TransferStudentsModal";
import TransferBranchModal from "../Common/TransferBranchModal";
import PageHeader from "../ui/PageHeader";
import { AppButton, ConfirmModal } from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";
import "./Students.scss";

const StudentsPage = () => {
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({ search: "", course: "", batch: "" });
  const [searchInput, setSearchInput] = useState("");
  const [students, setStudents] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [selected, setSelected] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showBranchTransfer, setShowBranchTransfer] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const { selectedBranchId, isMultiBranch, branches } = useBranch();

  // Fetch students from API with filters
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          pageSize,
          ...(filters.search && { search: filters.search }),
          ...(filters.course && { course: filters.course }),
          ...(filters.batch && { batch: filters.batch })
        };
        const { data } = await apiClient.get("/students/list", { params });
        setStudents(data.students || []);
        setTotal(data.total || 0);
        setAllBatches(data.allBatches || []);
        setAllCourses(data.allCourses || []);
      } catch (e) {
        setToast({ show: true, message: "Failed to load students.", type: "error" });
      }
      setLoading(false);
    };

    fetchStudents();
  }, [filters, page, pageSize, selectedBranchId, refreshKey]);

  const handleDelete = async (studentId) => {
    if (!studentId) return;
    setLoading(true);
    try {
      await apiClient.delete(`/students/${studentId}`);
      setToast({ show: true, message: "Student deleted successfully!", type: "success" });
      // Remove deleted student from list immediately
      setStudents(prev => prev.filter(s => s._id !== studentId));
    } catch (e) {
      setToast({ show: true, message: "Failed to delete student.", type: "error" });
    }
    setLoading(false);
  };

  const resetStudentPassword = (student) => {
    setConfirmAction({
      title: 'Reset Password',
      message: `Are you sure you want to reset the password for ${student.name}?`,
      variant: 'warning',
      confirmText: 'Reset',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await apiClient.put(`/users/${student.userId}/reset-password`);
          if (response.data.success) {
            setToast({ show: true, message: `Password reset link sent to ${student.email}`, type: 'success' });
          } else {
            setToast({ show: true, message: `${response.data.message}, Failed to send password reset link to ${student.email}`, type: 'warning' });
          }
        } catch (error) {
          console.error("Error resetting password:", error);
          setToast({ show: true, message: "An error occurred while resetting the password.", type: 'error' });
        }
      },
    });
  };

  const resendWelcomeEmail = (student) => {
    setConfirmAction({
      title: 'Resend Welcome Email',
      message: `Are you sure you want to resend the welcome email to ${student.name}?`,
      variant: 'info',
      confirmText: 'Resend',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await apiClient.post(`/students/${student._id}/resend-welcome-email`);
          if (response.data.success) {
            setToast({ show: true, message: `Welcome email resent to ${student.email}`, type: 'success' });
          } else {
            setToast({ show: true, message: `Failed to resend welcome email to ${student.email}`, type: 'warning' });
          }
        } catch (error) {
          console.error("Error resending welcome email:", error);
          setToast({ show: true, message: "An error occurred while resending the welcome email.", type: 'error' });
        }
      },
    });
  };

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) {
      setFilters(f => ({ ...f, search: searchInput }));
      setPage(1);
    }
  };

  const activeFilterCount = (filters.course ? 1 : 0) + (filters.batch ? 1 : 0);

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, course: '', batch: '' }));
    setPage(1);
  };

  return (
    <div className="sl-page">
      <PageHeader
        icon={Users}
        title="Students"
        description="Manage and track all enrolled students"
        actions={
          <div className="sl-header-actions">
            <AppButton variant="outline" size="sm" onClick={() => navigate("/students/import")}>
              <Upload size={16} /> Import
            </AppButton>
            <AppButton onClick={() => navigate("/students/add")}>
              <Plus size={16} /> Add Student
            </AppButton>
          </div>
        }
      />

      {/* Toolbar: Search + Filters + View Toggle */}
      <div className="sl-toolbar">
        <div className="sl-search">
          <Search size={16} className="sl-search-icon" />
          <input
            type="text"
            className="sl-search-input"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchBlur()}
          />
        </div>

        <div className="sl-toolbar-actions">
          {/* Desktop inline filters */}
          <select
            className="sl-filter-select d-none d-md-block"
            value={filters.course}
            onChange={(e) => {
              setFilters(f => ({ ...f, course: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">All Courses</option>
            {allCourses.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <select
            className="sl-filter-select d-none d-md-block"
            value={filters.batch}
            onChange={(e) => {
              setFilters(f => ({ ...f, batch: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">All Batches</option>
            {allBatches.map(b => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>

          {/* Mobile filter button */}
          <button
            className={`sl-filter-btn d-md-none${activeFilterCount > 0 ? ' sl-filter-btn--active' : ''}`}
            onClick={() => setShowFilterModal(true)}
            aria-label="Open filters"
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && (
              <span className="sl-filter-badge">{activeFilterCount}</span>
            )}
          </button>

          <div className="sl-view-toggle">
            <button
              className={`sl-view-btn ${view === "grid" ? "active" : ""}`}
              onClick={() => setView("grid")}
              aria-label="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`sl-view-btn ${view === "list" ? "active" : ""}`}
              onClick={() => setView("list")}
              aria-label="List view"
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {selected.length > 0 && (
          <>
            <AppButton
              variant="secondary"
              size="sm"
              onClick={() => setShowTransferModal(true)}
            >
              Transfer ({selected.length})
            </AppButton>
            {isMultiBranch && branches.length > 1 && (
              <AppButton
                variant="secondary"
                size="sm"
                onClick={() => setShowBranchTransfer(true)}
              >
                Change Branch ({selected.length})
              </AppButton>
            )}
          </>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="sl-empty">
          <div className="spinner-border" />
        </div>
      ) : students.length === 0 ? (
        <div className="sl-empty">
          <div className="sl-empty-icon-wrap">
            <Users size={32} />
          </div>
          <h3 className="sl-empty-title">No students found</h3>
          <p className="sl-empty-desc">
            Try adjusting your search or filter criteria
          </p>
        </div>
      ) : view === "list" ? (
        <StudentList
          students={students}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          total={total}
          selected={selected}
          setSelected={setSelected}
          onDelete={handleDelete}
          resetStudentPassword={resetStudentPassword}
          resendWelcomeEmail={resendWelcomeEmail}
          parentPage="students"
        />
      ) : (
        <StudentGrid
          students={students}
          onDelete={handleDelete}
          page={page}
          setPage={setPage}
          total={total}
          pageSize={pageSize}
        />
      )}

      {/* Footer (grid view only — list view has DataTable pagination) */}
      {!loading && students.length > 0 && view === "grid" && total <= pageSize && (
        <div className="sl-footer">
          Showing {students.length} of {total} entries
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
      <TransferStudentsModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        selectedIds={selected}
        onTransfer={() => {
          setSelected([]);
          setRefreshKey(k => k + 1);
          setToast({ show: true, message: "Students transferred successfully!", type: "success" });
        }}
        mode="students"
      />
      <TransferBranchModal
        show={showBranchTransfer}
        onHide={() => setShowBranchTransfer(false)}
        selectedIds={selected}
        mode="students"
        onTransfer={() => {
          setSelected([]);
          setRefreshKey(k => k + 1);
          setToast({ show: true, message: "Students moved to new branch!", type: "success" });
        }}
      />
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Course">
          <select
            value={filters.course}
            onChange={(e) => {
              setFilters(f => ({ ...f, course: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">All Courses</option>
            {allCourses.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Batch">
          <select
            value={filters.batch}
            onChange={(e) => {
              setFilters(f => ({ ...f, batch: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">All Batches</option>
            {allBatches.map(b => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        </FilterGroup>
      </FilterModal>

      <ConfirmModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.onConfirm()}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.confirmText || 'Confirm'}
        variant={confirmAction?.variant || 'danger'}
      />
    </div>
  );
};

export default StudentsPage;
